import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ICustomerEntity } from '@boma/supplier-portal/shared/entities';
import {
  CustomersService,
  OrdersService,
  PricingService,
} from '@boma/supplier-portal/shared/services';
import { MessageService, SelectItem } from 'primeng/api';
import { Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'boma-create-new-order',
  templateUrl: './create-new-order.component.html',
  styleUrls: ['./create-new-order.component.scss'],
})
export class CreateNewOrderComponent implements OnInit, OnChanges {
  private idempotencyKey = uuidv4();

  createOrderFormGroup = new FormGroup({
    entityId: new FormControl('', Validators.required),
    products: new FormControl([], Validators.required),
    supplyingLocationId: new FormControl('', Validators.required),
    receivingLocationId: new FormControl('', Validators.required),
    comment: new FormControl(''),
    isDirectSale: new FormControl(false),
    isPos: new FormControl(false),
    orderPaymentType: new FormControl('Cash'),
    externalOrderId: new FormControl(''),
    externalInvoiceId: new FormControl(''),
    //TODO need to add selected payment method here and pass the form group directly to the service cleaner code
  });

  selectedCustomer$: Observable<ICustomerEntity>;

  @Input() stepper = false;
  @Input() supplier;
  @Input() customer;
  @Input() receivingLocation;
  @Input() supplyingLocation;
  @Input() isSale: boolean = null; //used for sales not internal orders
  @Input() entityId: string;
  @Input() showStockLocations = false;
  @Input() isDebitNote = false; // Flag to indicate if creating a debit note
  @Output() orderOutcome = new EventEmitter();

  customerAndLocation: {
    entityId: string;
    locationId: string;
  } = {
    entityId: null,
    locationId: null,
  };

  selectedPaymentMethod = 'Cash';
  paymentOptions: SelectItem[];

  editSupplierLocation = false;
  editCustomer = false;
  editReceivingLocation = false;

  comment = '';
  isSubmitting = false;
  error: any;

  products: any[] = [];

  // Order lookup for debit notes
  originalOrderLookupLoading = false;
  originalOrderFound: any = null;
  originalOrderNotFound = false;

  // Multi-currency invoice support
  invoiceCurrencyOptions: SelectItem[] = [
    { label: 'ZMW – Zambian Kwacha', value: 'ZMW' },
    { label: 'USD – US Dollar', value: 'USD' },
    { label: 'EUR – Euro', value: 'EUR' },
    { label: 'GBP – British Pound', value: 'GBP' },
    { label: 'ZAR – South African Rand', value: 'ZAR' },
  ];
  selectedInvoiceCurrency = 'ZMW';
  invoiceExchangeRate: number = null;

  onInvoiceCurrencyChange() {
    this.invoiceExchangeRate = this.selectedInvoiceCurrency === 'ZMW' ? null : null;
  }

  constructor(
    private ordersService: OrdersService,
    private customersService: CustomersService,
    private pricingService: PricingService,
    private messageService: MessageService,
    private route: ActivatedRoute
  ) {
    this.customerAndLocation = {
      entityId: '',
      locationId: '',
    };
  }

  ngOnInit(): void {
    // Read query parameter to determine if creating debit note
    this.route.queryParams.subscribe(params => {
      if (params['isDebitNote'] !== undefined) {
        this.isDebitNote = params['isDebitNote'] === 'true';
        this.updateExternalInvoiceIdValidation();
      }
    });

    if (this.customer) this.updateCustomerDetails();

    const storedLocation = localStorage.getItem('lastSelectedOrderLocation');
    if (storedLocation) {
      this.supplyingLocation = storedLocation
        ? JSON.parse(storedLocation)
        : null;
      this.createOrderFormGroup.patchValue({
        supplyingLocationId: this.supplyingLocation?.id,
      });
    }

    if (this.supplier?.locations?.length === 1 && !this.supplyingLocation)
      this.supplyingLocation = this.supplier.locations[0];

    this.updateExternalInvoiceIdValidation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isDebitNote']) {
      this.updateExternalInvoiceIdValidation();
    }
  }

  private updateExternalInvoiceIdValidation(): void {
    const externalInvoiceIdControl = this.createOrderFormGroup.get('externalInvoiceId');
    const externalOrderIdControl = this.createOrderFormGroup.get('externalOrderId');

    if (this.isDebitNote) {
      // Only Original Order ID is required for debit notes
      // Debit note number is auto-generated
      externalOrderIdControl.setValidators([Validators.required]);
      externalInvoiceIdControl.clearValidators();
    } else {
      // Optional for regular sales orders
      externalInvoiceIdControl.clearValidators();
      externalOrderIdControl.clearValidators();
    }

    externalInvoiceIdControl.updateValueAndValidity();
    externalOrderIdControl.updateValueAndValidity();
  }

  handleSelectedCustomer(ev = null) {
    if (!this.supplier?.id && !this.entityId) return;

    this.customerAndLocation = {
      entityId: '',
      locationId: '',
    };
    this.receivingLocation = null;

    if (ev) {
      this.customersService
        .getCustomerById(this.supplier.id, ev[0].id)
        .subscribe({
          next: (x) => {
            this.customer = x;
            this.updateCustomerDetails();
          },
          error: (error) => {
            console.log(error);
          },
        });
    } else {
      this.updateCustomerDetails();
    }
  }

  updateCustomerDetails() {
    if (!this.customer) return;
    this.paymentOptions = [
      { label: 'Cash', value: 'Cash', disabled: false },
      { label: 'Card', value: 'Electronic', disabled: false },
      {
        label: 'Credit',
        value: 'Credit',
        disabled: !this.customer?.isAllowedToPurchaseOnCredit || false,
      },
    ];
    if (this.customer?.isAllowedToPurchaseOnCredit)
      this.selectedPaymentMethod = 'Credit';
    if (this.customer?.locations?.length === 1 && !this.receivingLocation)
      this.receivingLocation = this.customer.locations[0];

    this.customerAndLocation = {
      entityId: this.customer.id,
      locationId: this.receivingLocation?.id,
    };
  }

  handleSelectedLocation(locations) {
    this.supplyingLocation = locations?.length ? locations[0] : null;
    this.createOrderFormGroup.patchValue({
      supplyingLocationId: this.supplyingLocation?.id,
    });
    localStorage.setItem(
      'lastSelectedOrderLocation',
      JSON.stringify(this.supplyingLocation)
    );
  }

  handleProductSelectionChange(ev) {
    console.log(ev);
    this.products = ev;
  }

  createOrder() {
    if (
      !this.supplier ||
      !this.supplier.id ||
      !this.receivingLocation ||
      !this.receivingLocation.id ||
      !this.products?.length ||
      this.isSubmitting
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'The form is incomplete, please ensure you have provided all the details. eg: Customer and Customer Location',
      });
      return;
    }

    if (this.isDebitNote) {
      const externalOrderId = this.createOrderFormGroup.get('externalOrderId').value;

      if (!externalOrderId) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Original Order ID is required for Debit Notes',
        });
        return;
      }

      // Check if the original order was found (only block if lookup was attempted and failed)
      if (this.originalOrderNotFound) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'The Original Order ID could not be verified. Proceeding anyway.',
        });
      }
    }

    this.createOrderFormGroup.patchValue({
      entityId: this.supplier.id,
      receivingLocationId: this.receivingLocation.id,
      products: this.products,
    });

    const comment = this.createOrderFormGroup.get('comment').value;

    // Loop through products and add names of pricing tiers.
    let pricingComment = '';
    this.products.forEach((x) => {
      if (x.pricingTier) {
        pricingComment +=
          x.name +
          ' ' +
          (x.pricingTier?.pricingGroupName
            ? 'Pricing Group: ' + x.pricingTier?.pricingGroupName
            : 'Custom Customer Price') +
          ' : ' +
          x.pricingTier?.pricingTierId +
          '.';
      }
    });

    this.createOrderFormGroup.patchValue({
      comment: comment + pricingComment,
    });

    //Clean up products so we have a smaller cleaner package sent

    const items = this.createOrderFormGroup.get('products').value.map((x) => {
      return {
        productId: x.productId,
        units: x.units,
        price:
          x.paidPrice !== undefined && x.paidPrice !== null
            ? x.paidPrice
            : x.price,
        pricingTierId: x.pricingTier?.pricingTierId || null,
      };
    });

    // STRICT VALIDATION: Debit note should ONLY be true when:
    // 1. User clicked "Create Debit Note" button (isDebitNote flag is true)
    // 2. Original Order ID is filled
    const externalOrderIdValue = this.createOrderFormGroup.get('externalOrderId').value;
    const isValidDebitNote = this.isDebitNote && !!externalOrderIdValue;

    // Always send external IDs to the API from the form
    // The isDebitNote flag (not field presence) determines if it's a debit note
    const externalOrderId = isValidDebitNote ? externalOrderIdValue : '';
    const externalInvoiceId = this.createOrderFormGroup.get('externalInvoiceId').value || '';
    const finalIsDebitNote = isValidDebitNote; // Only true if all conditions met

    console.log('📝 [CREATE ORDER] Debit Note Validation:', {
      routeIsDebitNote: this.isDebitNote,
      externalOrderIdFilled: !!externalOrderIdValue,
      originalOrderFound: !!this.originalOrderFound,
      isValidDebitNote: isValidDebitNote,
      finalIsDebitNote: finalIsDebitNote
    });

    console.log('📝 [CREATE ORDER] Creating order with params:', {
      isDebitNote: finalIsDebitNote,
      externalOrderId: externalOrderId,
      externalInvoiceId: externalInvoiceId,
      paymentMethod: this.selectedPaymentMethod
    });

    this.isSubmitting = true;
    this.ordersService
      .createOrderForCustomer(
        this.createOrderFormGroup.get('entityId').value,
        this.createOrderFormGroup.get('supplyingLocationId').value, // operating location id...
        this.createOrderFormGroup.get('receivingLocationId').value,
        this.createOrderFormGroup.get('supplyingLocationId').value, // sales location id
        items,
        false, // isDirectSale
        false, // isPos
        this.selectedPaymentMethod,
        this.createOrderFormGroup.get('comment').value,
        externalOrderId,      // Always sent from form
        externalInvoiceId,    // Always sent from form
        this.idempotencyKey,
        finalIsDebitNote, // Only true if ALL conditions are met
        this.selectedInvoiceCurrency !== 'ZMW' ? this.selectedInvoiceCurrency : undefined,
        this.selectedInvoiceCurrency !== 'ZMW' ? this.invoiceExchangeRate : undefined
      )
      .subscribe({
        next: (v) => {
          this.isSubmitting = false;

          if (v?.orders.length) {
            console.log(`✅ [CREATE ORDER] ${finalIsDebitNote ? 'Debit note' : 'Order'} created successfully:`, v);
            this.orderOutcome.emit(v);
            this.idempotencyKey = uuidv4();
          }
        },
        error: (e) => {
          this.isSubmitting = false;
          console.error(`❌ [CREATE ORDER] Error creating ${finalIsDebitNote ? 'debit note' : 'order'}:`, e);
          this.error =
            e.status === 403
              ? 'You might not have sufficient permissions, please contact your support.'
              : 'Something went wrong: ' + e.status;
        },
      });
  }

  //Triggered by changing the variable and the child component updating
  getPricesForCustomerLocation() {
    this.customerAndLocation = {
      entityId: this.customer.id,
      locationId: this.receivingLocation.id,
    };
  }

  /**
   * Look up the original order by external order ID
   * Used for validating debit notes
   */
  lookupOriginalOrder(externalOrderId: string) {
    console.log('🔍 [LOOKUP] lookupOriginalOrder called with:', externalOrderId);
    console.log('🔍 [LOOKUP] isDebitNote:', this.isDebitNote);
    console.log('🔍 [LOOKUP] supplier?.id:', this.supplier?.id);

    if (!externalOrderId || !this.isDebitNote || !this.supplier?.id) {
      console.warn('⚠️ [LOOKUP] Validation failed - clearing results');
      this.originalOrderFound = null;
      this.originalOrderNotFound = false;
      return;
    }

    // Clear previous results
    this.originalOrderFound = null;
    this.originalOrderNotFound = false;
    this.originalOrderLookupLoading = true;

    console.log('📡 [LOOKUP] Starting API call to search for order...');
    const searchParams = {
      entityId: this.supplier.id,
      query: externalOrderId,
      page: 1,
      perPage: 10,
      statuses: ['Fulfilled'],
    };
    console.log('📡 [LOOKUP] Search params:', searchParams);

    // Search for orders with this external order ID
    this.ordersService
      .getSales(searchParams)
      .subscribe({
        next: (response) => {
          console.log('✅ [LOOKUP] API Response received:', response);
          console.log('✅ [LOOKUP] Number of orders returned:', response?.items?.length || 0);

          this.originalOrderLookupLoading = false;

          if (response?.items?.length > 0) {
            console.log('🔎 [LOOKUP] Searching for exact match in results...');

            // Find exact match - check both reference and externalOrderId
            const matchedOrder = response.items.find(
              (order) => {
                const orderExtId = order.locationDetails?.receivingLocationDetails?.externalOrderId;
                const orderRef = order.reference;
                const matchesExtId = orderExtId === externalOrderId;
                const matchesRef = orderRef === externalOrderId;

                console.log('🔎 [LOOKUP] Checking order:', {
                  reference: orderRef,
                  externalOrderId: orderExtId,
                  searchTerm: externalOrderId,
                  matchesReference: matchesRef,
                  matchesExternalId: matchesExtId,
                  matches: matchesRef || matchesExtId
                });

                return matchesRef || matchesExtId;
              }
            );

            if (matchedOrder) {
              console.log('✅ [LOOKUP] Exact match found!', matchedOrder);
              this.originalOrderFound = matchedOrder;
              this.originalOrderNotFound = false;

              // Auto-fill the debit note number based on the original invoice
              const originalInvoiceId = matchedOrder.locationDetails?.receivingLocationDetails?.externalInvoiceId;
              console.log('📋 [LOOKUP] Original Invoice ID:', originalInvoiceId);

              if (originalInvoiceId) {
                // You can format the debit note number here if needed
                // For now, we'll leave it blank as it should be auto-generated by backend
              }

              this.messageService.add({
                severity: 'success',
                summary: 'Order Found',
                detail: `Original order ${matchedOrder.reference} found and verified.`,
              });
            } else {
              console.warn('❌ [LOOKUP] No exact match found in results');
              this.originalOrderNotFound = true;
              this.messageService.add({
                severity: 'error',
                summary: 'Order Not Found',
                detail: 'No fulfilled order found with this Order ID.',
              });
            }
          } else {
            console.warn('❌ [LOOKUP] No orders returned from API');
            this.originalOrderNotFound = true;
            this.messageService.add({
              severity: 'error',
              summary: 'Order Not Found',
              detail: 'No fulfilled order found with this Order ID.',
            });
          }
        },
        error: (error) => {
          console.error('❌ [LOOKUP] API Error:', error);
          console.error('❌ [LOOKUP] Error details:', {
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            error: error?.error
          });

          this.originalOrderLookupLoading = false;
          this.originalOrderNotFound = true;

          this.messageService.add({
            severity: 'error',
            summary: 'Lookup Failed',
            detail: 'Failed to verify the original order. Please try again.',
          });
        },
      });
  }
}

